import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner, Table } from 'react-bootstrap';
import { apiGet, apiPost } from '../../services/api';
import AdminCompanyDetailModal from './AdminCompanyDetailModal';
import AdminGiftPackageModal from './AdminGiftPackageModal';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function boolBadge(value, trueLabel, falseLabel) {
  return (
    <Badge bg={value ? 'success' : 'secondary'} className="rwm-admin__badge">
      {value ? trueLabel : falseLabel}
    </Badge>
  );
}

export default function AdminCompaniesTab({ token, setError, handleAuthError, isActive }) {
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [giftTarget, setGiftTarget] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftSuccessMessage, setGiftSuccessMessage] = useState('');

  const loadCompanies = useCallback(async () => {
    if (!token) return;
    setCompaniesLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/v1/admin/companies', token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not load companies.');
        return;
      }
      const json = await res.json();
      setCompanies(json.data ?? []);
      setCompaniesLoaded(true);
    } catch {
      setError('Could not load companies.');
    } finally {
      setCompaniesLoading(false);
    }
  }, [token, handleAuthError, setError]);

  useEffect(() => {
    if (isActive && !companiesLoaded && token) {
      loadCompanies();
    }
  }, [isActive, companiesLoaded, token, loadCompanies]);

  useEffect(() => {
    if (!giftSuccessMessage) return undefined;
    const timer = window.setTimeout(() => setGiftSuccessMessage(''), 5000);
    return () => window.clearTimeout(timer);
  }, [giftSuccessMessage]);

  const closeCompanyModal = () => {
    setShowCompanyModal(false);
    setSelectedCompany(null);
  };

  const handleCompanyModalAction = () => {
    closeCompanyModal();
    loadCompanies();
  };

  const openGiftModal = (company) => {
    setGiftTarget({ id: company.id, name: company.name });
    setShowGiftModal(true);
  };

  const handleGiftSuccess = (message) => {
    setGiftSuccessMessage(message);
    setShowGiftModal(false);
    setGiftTarget(null);
  };

  const runCompanyAction = async (companyId, action) => {
    setActionLoadingId(`company-${companyId}-${action}`);
    setError('');
    try {
      const res = await apiPost(`/api/v1/admin/companies/${companyId}/${action}`, {}, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError(`Could not ${action} company.`);
        return;
      }
      await loadCompanies();
    } catch {
      setError(`Could not ${action} company.`);
    } finally {
      setActionLoadingId(null);
    }
  };

  if (companiesLoading) {
    return <Spinner animation="border" size="sm" data-testid="rwm-admin-companies-loading" />;
  }

  return (
    <>
      {giftSuccessMessage ? (
        <Alert
          variant="success"
          dismissible
          className="mb-3"
          onClose={() => setGiftSuccessMessage('')}
          data-testid="rwm-admin-gift-success"
        >
          {giftSuccessMessage}
        </Alert>
      ) : null}

      <div className="table-responsive">
        <Table className="rwm-admin__table align-middle mb-0" data-testid="rwm-admin-companies-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Country</th>
              <th>Approved</th>
              <th>Active</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr key={company.id} data-testid={`rwm-admin-company-row-${company.id}`}>
                <td>
                  <button
                    type="button"
                    className="btn btn-link p-0 border-0 rwm-admin__job-title-link text-start"
                    onClick={() => {
                      setSelectedCompany(company);
                      setShowCompanyModal(true);
                    }}
                    data-testid={`rwm-admin-company-name-${company.id}`}
                  >
                    {company.name}
                  </button>
                </td>
                <td>{company.country || '—'}</td>
                <td>{boolBadge(company.is_approved, 'Yes', 'No')}</td>
                <td>{boolBadge(company.is_active, 'Yes', 'No')}</td>
                <td>{formatDate(company.created_at)}</td>
                <td className="rwm-admin__actions">
                  <Button
                    size="sm"
                    variant="outline-info"
                    className="me-2 mb-1"
                    onClick={() => openGiftModal(company)}
                    data-testid={`rwm-admin-gift-${company.id}`}
                  >
                    Gift
                  </Button>
                  {!company.is_approved ? (
                    <Button
                      size="sm"
                      variant="info"
                      className="me-2 mb-1"
                      disabled={actionLoadingId === `company-${company.id}-approve`}
                      onClick={() => runCompanyAction(company.id, 'approve')}
                      data-testid={`rwm-admin-company-approve-${company.id}`}
                    >
                      Approve
                    </Button>
                  ) : null}
                  {company.is_active ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline-warning"
                        className="me-2 mb-1"
                        disabled={actionLoadingId === `company-${company.id}-suspend`}
                        onClick={() => runCompanyAction(company.id, 'suspend')}
                        data-testid={`rwm-admin-company-suspend-${company.id}`}
                      >
                        Suspend
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="mb-1"
                        disabled={actionLoadingId === `company-${company.id}-ban`}
                        onClick={() => runCompanyAction(company.id, 'ban')}
                        data-testid={`rwm-admin-company-ban-${company.id}`}
                      >
                        Ban
                      </Button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {showCompanyModal && selectedCompany ? (
        <AdminCompanyDetailModal
          company={selectedCompany}
          token={token}
          onClose={closeCompanyModal}
          onAction={handleCompanyModalAction}
          onGiftCredits={() => {
            openGiftModal(selectedCompany);
            closeCompanyModal();
          }}
          handleAuthError={handleAuthError}
          setError={setError}
        />
      ) : null}

      {showGiftModal && giftTarget ? (
        <AdminGiftPackageModal
          company={giftTarget}
          token={token}
          onClose={() => {
            setShowGiftModal(false);
            setGiftTarget(null);
          }}
          onSuccess={handleGiftSuccess}
        />
      ) : null}
    </>
  );
}
